// Events and actions of form fields and links, saved the way the format keeps them (/A, /AA with GoTo, URI, SubmitForm, ResetForm,
// Hide, Named and JavaScript actions chained with /Next) and read back. Actions the format has no counterpart for (set a value, play a
// sound, load or save a data source, choose or upload a picture) are written as a script that does them plus a private /FGAct marker, so
// the desktop Reader (rd_pdf::fieldactions) and this one read each other's files and other tools still show a script.

import { PdfDocument, textString } from "./document";
import { Dict, PName, PRef, type PObj } from "./parse";
import { pdfText } from "./form";

export type Trigger = "click" | "mouseDown" | "mouseEnter" | "mouseExit" | "focus" | "blur" | "change" | "format" | "validate" | "calculate";

export const TRIGGERS: Array<{ id: Trigger; key: string; label: string; describe: string }> = [
    { id: "click", key: "U", label: "On click", describe: "Runs when the mouse button is released over the field." },
    { id: "mouseDown", key: "D", label: "On mouse down", describe: "Runs when the mouse button is pressed over the field." },
    { id: "mouseEnter", key: "E", label: "On mouse enter", describe: "Runs when the pointer moves onto the field." },
    { id: "mouseExit", key: "X", label: "On mouse exit", describe: "Runs when the pointer leaves the field." },
    { id: "focus", key: "Fo", label: "On focus", describe: "Runs when the field is entered (it gets the keyboard focus)." },
    { id: "blur", key: "Bl", label: "On blur", describe: "Runs when the field is left (it loses the keyboard focus)." },
    { id: "change", key: "K", label: "On change", describe: "Runs as the value is typed: it can accept or refuse each key." },
    { id: "format", key: "F", label: "On format", describe: "Runs before the value is shown: it decides how it looks." },
    { id: "validate", key: "V", label: "On validate", describe: "Runs when the value is accepted: it can refuse the value." },
    { id: "calculate", key: "C", label: "On calculate", describe: "Runs when another field changes: it sets this field's value." }
];

export type SubmitFormat = "FDF" | "XFDF" | "HTML" | "PDF" | "JSON" | "CSV";
export type SubmitTarget = "REST" | "WORKBOOK" | "DATABASE";

export interface SubmitSpec { target: SubmitTarget; url: string; get: boolean; format: SubmitFormat; headers: Array<[string, string]>; fields: string[] }

export type PdfAction =
    | { type: "goto"; page: number }
    | { type: "url"; url: string }
    | { type: "submit"; spec: SubmitSpec }
    | { type: "reset" }
    | { type: "showhide"; field: string; show: boolean }
    | { type: "setvalue"; field: string; expr: string }
    | { type: "sound" }
    | { type: "script"; code: string }
    | { type: "print" }
    | { type: "save" }
    | { type: "pickimage" }
    | { type: "loaddata" }
    | { type: "savedata" }
    | { type: "upload" };

export const ACTION_KINDS: Array<{ type: PdfAction["type"]; label: string }> = [
    { type: "goto", label: "Go to page" }, { type: "url", label: "Open web address" }, { type: "submit", label: "Submit form" }, { type: "reset", label: "Reset form" },
    { type: "showhide", label: "Show or hide field" }, { type: "setvalue", label: "Set field value" }, { type: "sound", label: "Play sound" }, { type: "script", label: "Run JavaScript" },
    { type: "print", label: "Print" }, { type: "save", label: "Save" }, { type: "pickimage", label: "Choose a picture" }, { type: "loaddata", label: "Load from data source" },
    { type: "savedata", label: "Save to data source" }, { type: "upload", label: "Upload picture" }
];

export function newAction(type: PdfAction["type"]): PdfAction {
    switch (type) {
        case "goto": return { type, page: 0 };
        case "url": return { type, url: "https://" };
        case "submit": return { type, spec: { target: "REST", url: "https://", get: false, format: "JSON", headers: [], fields: [] } };
        case "showhide": return { type, field: "", show: true };
        case "setvalue": return { type, field: "", expr: "" };
        case "script": return { type, code: "" };
        default: return { type } as PdfAction;
    }
}

export function summary(a: PdfAction): string {
    switch (a.type) {
        case "goto": return `Go to page ${a.page + 1}`;
        case "url": return `Open ${a.url}`;
        case "submit": return `Submit as ${a.spec.format} to ${a.spec.url || a.spec.target}`;
        case "reset": return "Reset form";
        case "showhide": return `${a.show ? "Show" : "Hide"} ${a.field}`;
        case "setvalue": return `Set ${a.field} to ${a.expr}`;
        case "sound": return "Play sound";
        case "script": { const one = a.code.split(/\s+/).join(" ").trim(); return `Script: ${one.length > 40 ? one.slice(0, 40) + "..." : one}`; }
        case "print": return "Print";
        case "save": return "Save";
        case "pickimage": return "Choose a picture";
        case "loaddata": return "Load from data source";
        case "savedata": return "Save to data source";
        case "upload": return "Upload picture";
    }
}

/** What other tools run for an image field's click (choose a picture for the button's icon). */
export const PICK_IMAGE_JS = "event.target.buttonImportIcon();";

/** Links with a script or a non-web scheme are refused (the desktop Reader's rule for URI actions). */
export function safeUri(u: string): boolean {
    const l = u.trim().toLowerCase();
    return l.startsWith("https://") || l.startsWith("http://") || l.startsWith("mailto:") || l.startsWith("tel:");
}

const jsEscape = (s: string) => s.replace(/\\/g, "\\\\").replace(/"/g, '\\"').replace(/\n/g, "\\n").replace(/\r/g, "");

/** The script for "set field to expression": a quoted text or a number stays a literal, anything else is read as another field's value. */
export function setValueScript(field: string, expr: string): string {
    const e = expr.trim();
    const value = /^-?\d+(\.\d+)?$/.test(e) ? e : /^"[^"]*"$/.test(e) ? e : `this.getField("${jsEscape(e)}").value`;
    return `this.getField("${jsEscape(field)}").value = ${value};`;
}

const pn = (s: string) => new PName(s);

/** One action as a dictionary; `pageRef(i)` gives the object a page number refers to. */
export function actionDict(a: PdfAction, pageRef: (i: number) => PRef | null): Dict {
    const d = new Dict(null);
    d.set("Type", pn("Action"));
    const marker = (js: string, tag: string) => { d.set("S", pn("JavaScript")); d.set("JS", pdfText(js)); d.set("FGAct", pn(tag)); };
    switch (a.type) {
        case "goto": { d.set("S", pn("GoTo")); const p = pageRef(a.page); if (p) d.set("D", [p, pn("XYZ"), null, null, null]); break; }
        case "url": d.set("S", pn("URI")); d.set("URI", a.url); break;
        case "submit": {
            const s = a.spec;
            d.set("S", pn("SubmitForm"));
            const fs = new Dict(null);
            fs.set("FS", pn("URL")); fs.set("F", s.url);
            d.set("F", fs);
            let flags = s.format === "XFDF" ? 32 : s.format === "HTML" || s.format === "JSON" || s.format === "CSV" ? 4 : s.format === "PDF" ? 256 : 0;
            if (s.get) flags |= 8;
            d.set("Flags", flags);
            d.set("FGFormat", pn(s.format)); d.set("FGMethod", pn(s.get ? "GET" : "POST")); d.set("FGTarget", pn(s.target));
            if (s.headers.length) d.set("FGHeaders", s.headers.map(([k, v]) => pdfText(`${k}: ${v}`)));
            if (s.fields.length) d.set("Fields", s.fields.map((f) => pdfText(f)));
            break;
        }
        case "reset": d.set("S", pn("ResetForm")); break;
        case "showhide": d.set("S", pn("Hide")); d.set("T", pdfText(a.field)); d.set("H", !a.show); break;
        case "setvalue": marker(setValueScript(a.field, a.expr), "SetValue"); d.set("FGField", pdfText(a.field)); d.set("FGExpr", pdfText(a.expr)); break;
        case "sound": marker("app.beep(0);", "Sound"); break;
        case "script": d.set("S", pn("JavaScript")); d.set("JS", pdfText(a.code)); break;
        case "print": d.set("S", pn("Named")); d.set("N", pn("Print")); break;
        case "save": d.set("S", pn("Named")); d.set("N", pn("Save")); break;
        case "pickimage": marker(PICK_IMAGE_JS, "PickImage"); break;
        case "loaddata": marker("// FowlGuard: load the form from its data source", "LoadData"); break;
        case "savedata": marker("// FowlGuard: save the form to its data source", "SaveData"); break;
        case "upload": marker("// FowlGuard: upload the picture to its data source", "UploadFile"); break;
    }
    return d;
}

/** A chain of actions as one object: the first with /Next leading to the rest. */
export function actionChain(list: PdfAction[], pageRef: (i: number) => PRef | null): PObj {
    let next: PObj = null;
    for (const a of [...list].reverse()) {
        const d = actionDict(a, pageRef);
        if (next) d.set("Next", next);
        next = d;
    }
    return next;
}

export type EventMap = Partial<Record<Trigger, PdfAction[]>>;

/**
 * Puts `events` on a field's widget dictionary: exactly the triggers listed have actions afterwards. The scripts of a number or date
 * format (`Format`, `Change`) are kept unless `events` gives its own.
 */
export function applyEvents(holder: Dict, events: EventMap, pageRef: (i: number) => PRef | null, keepFormatScripts: boolean): void {
    const aa = new Dict(null);
    const old = holder.dict("AA");
    if (old) for (const k of old.keys()) aa.set(k, old.raw(k) as PObj);
    for (const t of TRIGGERS) {
        const given = events[t.id];
        if (t.id === "click") {
            aa.map.delete("U");
            if (given?.length) holder.set("A", actionChain(given, pageRef)); else holder.map.delete("A");
            continue;
        }
        if ((t.id === "format" || t.id === "change") && keepFormatScripts && !given?.length) continue;
        if (given?.length) aa.set(t.key, actionChain(given, pageRef)); else aa.map.delete(t.key);
    }
    if (aa.keys().length) holder.set("AA", aa); else holder.map.delete("AA");
}

// ---- reading ------------------------------------------------------------------------------------------------------------------------

function readAction(doc: PdfDocument, d: Dict): PdfAction | null {
    const xref = doc.xref;
    const s = d.name("S");
    const text = (k: string) => textString(d.get(k));
    const strs = (v: PObj): string[] => (Array.isArray(v) ? v.map((x) => xref.resolve(x)).filter((x): x is string => typeof x === "string").map(textString) : []);
    switch (s) {
        case "GoTo": {
            let page = 0;
            const dest = d.arr("D");
            const first = dest ? dest[0] : null;
            if (first instanceof PRef) { doc.pages(); const i = doc.pageRefs.findIndex((r) => r?.num === first.num); if (i >= 0) page = i; }
            else if (typeof first === "number") page = first;
            return { type: "goto", page };
        }
        case "URI": return { type: "url", url: textString(d.get("URI")) };
        case "SubmitForm": {
            const f = d.dict("F");
            const flags = d.num("Flags");
            const fmt = d.name("FGFormat") as SubmitFormat | null;
            const format: SubmitFormat = fmt && ["FDF", "XFDF", "HTML", "PDF", "JSON", "CSV"].includes(fmt) ? fmt : flags & 256 ? "PDF" : flags & 32 ? "XFDF" : flags & 4 ? "HTML" : "FDF";
            const tg = d.name("FGTarget");
            const headers = strs(d.get("FGHeaders")).flatMap((l): Array<[string, string]> => { const i = l.indexOf(":"); return i < 0 ? [] : [[l.slice(0, i).trim(), l.slice(i + 1).trim()]]; });
            return { type: "submit", spec: { target: tg === "WORKBOOK" || tg === "DATABASE" ? tg : "REST", url: f ? textString(f.get("F")) : "", get: d.name("FGMethod") ? d.name("FGMethod") === "GET" : (flags & 8) !== 0, format, headers, fields: strs(d.get("Fields")) } };
        }
        case "ResetForm": return { type: "reset" };
        case "Hide": return { type: "showhide", field: text("T"), show: d.get("H") === false };
        case "Named": { const n = d.name("N"); return n === "Print" ? { type: "print" } : n === "Save" || n === "SaveAs" ? { type: "save" } : null; }
        case "JavaScript": {
            switch (d.name("FGAct")) {
                case "SetValue": return { type: "setvalue", field: text("FGField"), expr: text("FGExpr") };
                case "Sound": return { type: "sound" };
                case "PickImage": return { type: "pickimage" };
                case "LoadData": return { type: "loaddata" };
                case "SaveData": return { type: "savedata" };
                case "UploadFile": return { type: "upload" };
                default: return { type: "script", code: text("JS") };
            }
        }
        default: return null;
    }
}

function readChain(doc: PdfDocument, o: PObj): PdfAction[] {
    const out: PdfAction[] = [];
    let cur: PObj = doc.xref.resolve(o);
    for (let guard = 0; guard < 32 && cur; guard++) {
        if (Array.isArray(cur)) { for (const it of cur) out.push(...readChain(doc, it)); break; }
        if (!(cur instanceof Dict)) break;
        const a = readAction(doc, cur);
        if (a) out.push(a);
        const n = cur.raw("Next");
        cur = n === undefined ? null : doc.xref.resolve(n);
    }
    return out;
}

/** The events of a widget or annotation dictionary that have actions. */
export function readEvents(doc: PdfDocument, holder: Dict, field?: Dict): EventMap {
    const out: EventMap = {};
    for (const t of TRIGGERS) {
        for (const d of [holder, field].filter((x): x is Dict => Boolean(x))) {
            let found: PdfAction[] = [];
            if (t.id === "click" && d.raw("A") !== undefined) found = readChain(doc, d.raw("A") as PObj);
            if (!found.length) { const aa = d.dict("AA"); if (aa?.raw(t.key) !== undefined) found = readChain(doc, aa.raw(t.key) as PObj); }
            if (found.length) { out[t.id] = found; break; }
        }
    }
    return out;
}
